// src/components/AuthSwitch.tsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff, Google } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import {
  listenForAuthChanges,
  loginWithEmail,
  loginWithGoogle,
  registerWithEmail,
} from "../redux/authSlice";
import { useNavigate } from "react-router-dom";


//const BLUE_GOLD_GRADIENT = "linear-gradient(135deg, #003580 0%, #1a4fa3 40%, #FEBA02 100%)";
//const BLUE_GOLD_SOFT = "linear-gradient(135deg, #002a66 0%, #003580 35%, #FEBA02 100%)";

const AuthSwitch: React.FC = () => {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { status, user, authLoaded } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (authLoaded && user) {
      navigate("/");
    }
  }, [authLoaded, user, navigate]);

  useEffect(() => {
    dispatch(listenForAuthChanges());
  }, [dispatch]);

  // toggle
  const handleToggle = (newMode: "login" | "signup") => {
    setMode(newMode);
    setError("");
    setPassword("");
    setConfirmPassword("");
  };

  // ---------- Password Validation ----------
  const extractUsername = (mail: string) =>
    mail.split("@")[0]?.toLowerCase() || "";

  const validatePassword = (pwd: string, email: string) => {
    const username = extractUsername(email);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
    const validLength = pwd.length >= 6 && pwd.length <= 12;
    const notEmail = pwd.toLowerCase() !== email.toLowerCase();
    const notContainEmail =
      !pwd.toLowerCase().includes(email.toLowerCase()) &&
      !pwd.toLowerCase().includes(username);

    return {
      hasUpper,
      hasLower,
      hasNumber,
      hasSpecial,
      validLength,
      notEmail,
      notContainEmail,
    };
  };

  const check = validatePassword(password, email);
  const allValid =
    check.hasUpper &&
    check.hasLower &&
    check.hasNumber &&
    check.hasSpecial &&
    check.validLength &&
    check.notEmail &&
    check.notContainEmail;

  const strengthScore = [
    check.hasUpper,
    check.hasLower,
    check.hasNumber,
    check.hasSpecial,
    check.validLength,
  ].filter(Boolean).length;

  const strengthLabel =
    ["Too Weak", "Weak", "Good", "Strong", "Very Strong"][strengthScore] ||
    "Too Weak";

  const handleLogin = async () => {
    setError("");
    if (!email || !password) return setError("Please fill in all fields");
    try {
      await dispatch(loginWithEmail({ email, password })).unwrap();
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Login failed");
    }
  };

  const handleRegister = async () => {
    setError("");
    if (!email || !password) return setError("Please fill in all fields");
    if (password !== confirmPassword)
      return setError("Passwords do not match");
    if (!allValid) return setError("Password must meet all requirements.");

    try {
      const [firstName = "", lastName = ""] = email.split("@")[0].split(".");
      await dispatch(
        registerWithEmail({
          email,
          password,
          firstName,
          lastName,
          phoneNumber: "",
          profilePicture: "",
          username: ""
        })
      ).unwrap();
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    }
  };

  const handleGoogle = async () => {
    try {
      await dispatch(loginWithGoogle()).unwrap();
      navigate("/");
    } catch (err: any) {
      setError(err.message || "Google login failed");
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
background: "linear-gradient(135deg, #003580 0%, #1a4fa3 50%, #FEBA02 100%)",
//background: "linear-gradient(135deg, #002a66 0%, #003580 60%, #4d79c7 100%)",
//background: "linear-gradient(135deg, #003580 0%, #1a4fa3 50%, #FEBA02 100%)",
//background: "linear-gradient(135deg, #000000, #660066, #d896ff, #efbbff)",        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          background: "rgba(255,255,255,0.95)",
          borderRadius: 4,
          p: 4,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Switch */}
        <Box display="flex" justifyContent="center" mb={3}>
          <div
            style={{
              width: 320,
              height: 64,
              borderRadius: 9999,
              background: "rgba(255,255,255,0.25)",
              backdropFilter: "blur(12px)",
              display: "flex",
              alignItems: "center",
              position: "relative",
              cursor: "pointer",
border: "1px solid rgba(105, 105, 105, 0.5)",
              boxShadow: "0 8px 24px rgba(192,192,192,0.35)", // silver shadow
              overflow: "hidden",
            }}
          >
            <motion.div
              key={mode}
              initial={{ x: mode === "login" ? "-100%" : "100%" }}
              animate={{ x: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              style={{
                position: "absolute",
                top: 5,
                left: mode === "login" ? 5 : "calc(50% + 3px)",
                width: "calc(50% - 8px)",
                height: "calc(100% - 10px)",
                borderRadius: 9999,
background: "linear-gradient(135deg, #003580 0%,  #FEBA02 100%)",
              }}
            />
            <div
              onClick={() => handleToggle("login")}
              style={{
                flex: 1,
                textAlign: "center",
                fontWeight: 700,
                fontSize: 18,
                color: mode === "login" ? "#fff" : "#333",
                zIndex: 2,
                userSelect: "none",
              }}
            >
              Login
            </div>
            <div
              onClick={() => handleToggle("signup")}
              style={{
                flex: 1,
                textAlign: "center",
                fontWeight: 700,
                fontSize: 18,
                color: mode === "signup" ? "#fff" : "#333",
                zIndex: 2,
                userSelect: "none",
              }}
            >
              Signup
            </div>
          </div>
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          fontWeight={700}
          textAlign="center"
          mb={2}
          color="#000000ff" // dark gold text
        >
          {mode === "login" ? "Sign in to CosMeta" : "Sign up to CosMeta"}
        </Typography>

        {/* Form */}
        <TextField
          label="Email"
          fullWidth
          sx={{ mb: 2 }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={mode === "signup" && password.length > 0 && !allValid}
          helperText={
            mode === "signup" && password ? `Strength: ${strengthLabel}` : ""
          }
          sx={{ mb: mode === "signup" ? 1 : 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Password checklist only for signup */}
        {mode === "signup" && password && (
          <Box sx={{ mb: 2, fontSize: 13, color: "#444" }}>
            <div>Requirements:</div>
            <ul style={{ margin: "6px 0 0 14px", padding: 0 }}>
              <li style={{ color: check.hasUpper ? "green" : "red" }}>
                At least 1 uppercase letter
              </li>
              <li style={{ color: check.hasLower ? "green" : "red" }}>
                At least 1 lowercase letter
              </li>
              <li style={{ color: check.hasNumber ? "green" : "red" }}>
                At least 1 number
              </li>
              <li style={{ color: check.hasSpecial ? "green" : "red" }}>
                At least 1 special character
              </li>
              <li style={{ color: check.validLength ? "green" : "red" }}>
                6–12 characters long
              </li>
              <li style={{ color: check.notEmail ? "green" : "red" }}>
                Not your email
              </li>
              <li style={{ color: check.notContainEmail ? "green" : "red" }}>
                Doesn’t contain email/username
              </li>
            </ul>
          </Box>
        )}

        {mode === "signup" && (
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword.length > 0 && confirmPassword !== password}
            sx={{ mb: 3 }}
          />
        )}

        {error && (
          <Typography color="error" fontSize={13} textAlign="center" mb={2}>
            {error}
          </Typography>
        )}

        {/* Main CTA button */}
        <Button
          fullWidth
          onClick={mode === "login" ? handleLogin : handleRegister}
          disabled={status === "loading"}
          sx={{
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2,
background: "linear-gradient(135deg, #003580 0%, #1a4fa3 50%, #FEBA02 100%)",
            color: "#000",
            boxShadow: "0 6px 18px rgba(0, 51, 255, 1)",
            "&:hover": {
              background: "linear-gradient(90deg, #f5f5f5, #0055ffff)",
            },
            mb: 2,
          }}
        >
          {status === "loading"
            ? mode === "login"
              ? "Signing in..."
              : "Registering..."
            : mode === "login"
            ? "Login"
            : "Signup"}
        </Button>

        {/* Google button */}
        <Button
          fullWidth
          onClick={handleGoogle}
          variant="outlined"
          startIcon={<Google />}
          sx={{
            py: 1.5,
            fontWeight: 600,
            borderColor: "#C0C0C0",
            color: "#555",
            "&:hover": {
              background: "linear-gradient(90deg,#f0f0f0,#c0c0c0)",
              borderColor: "#A9A9A9",
              color: "#000",
            },
          }}
        >
          Continue with Google
        </Button>

        {/* Footer Links */}
        {mode === "login" && (
          <Typography sx={{ textAlign: "center", fontSize: 13, mt: 2 }}>
            <span
              style={{ cursor: "pointer", color: "#000000ff" }}
              onClick={() => navigate("/ResetPassword")}
            >
              Forgot password?
            </span>
          </Typography>
        )}
        {mode === "signup" && (
          <Typography sx={{ textAlign: "center", fontSize: 13, mt: 2 }}>
            Have an account?{" "}
            <span
              style={{ cursor: "pointer", color: "#000000ff" }}
              onClick={() => handleToggle("login")}
            >
              Login
            </span>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AuthSwitch;
